import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import query from "../../../../../../server/constants.api";
import useFetch from "../../../../../../server/useFetch";
import { handleNotification } from "../../../../../../utils/config/notificationHandler";
import { EditPopupConfigurations } from "../../EditPopupConfigurations";
import CustomActionFooter from "../CustomActionFooter/CustomActionFooter";
import CustomNavigationFooter from "../CustomNavigationFooter/CustomNavigationFooter";
import { serializeRecordForThresholdsModal } from "../ThresholdsConfig/ThresholdsConfig";
import { default as useThresholdsCreateModal } from "./useThresholdsCreateModal/useThresholdsCreateModal";
import { default as useThresholdsItemModal } from "./useThresholdsItemModal/useThresholdsItemModal";

const ThresholdsModal = ({ isCreateModalOpen, isEditModalOpen, isViewModalOpen, onCreateModalClose, onEditModalClose, onViewModalClose,
    selectedItem, setSelectedItem, selectedRecord, onCreateOrEditSuccess
}) => {

    const { t } = useTranslation();

    const [bodyToPost, setBodyToPost] = useState(null);
    const tenant = JSON.parse(localStorage.getItem('unified_nova_tenant'));
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [createStep, setCreateStep] = useState(1);

    const {
        isFetching: addOrEditThresholdIsFetching,
        refetch: refetchAddOrEditThreshold,
        error: addOrEditThresholdError,
        isSuccess: addOrEditThresholdIsSuccess,
        isError: addOrEditThresholdIsError,
        remove: removeAddOrEditThreshold
    } = useFetch(
        "/configurations/thresholds",
        'editThreshold',
        bodyToPost,
        false,
        isCreateModalOpen ? 'POST' : 'PUT',
        query.middlewareURL
    );

    const {
        data: subTenants,
        remove: removeSubTenants,
        refetch: refetchSubTenants
    } = useFetch(
        '/items/distinct-properties',
        'getSubTenantsForThresholds',
        { tenant: tenant[0], properties: "tenant", },
        false,
        null,
        query.middlewareURL
    )

    const {
        data: modalConfigs,
        refetch: refetchModalConfigs,
        remove: removeModalConfigs
    } = useFetch(
        '/configurations/thresholds/modal',
        'getModalConfigs',
        { ID: selectedItem?.id },
        false,
        null,
        query.middlewareURL)

    /**
     * Handle the notification for the add or edit threshold fetch
     */
    useEffect(() => {
        handleNotification(addOrEditThresholdIsFetching, addOrEditThresholdIsSuccess, addOrEditThresholdIsError, addOrEditThresholdError,
            isCreateModalOpen ? "notification.success_add_threshold" : "notification.success_edit_threshold",
            isCreateModalOpen ? "notification.error_add_threshold" : "notification.error_edit_threshold",
            t
        );

        if (!addOrEditThresholdIsFetching && addOrEditThresholdIsSuccess) {
            handleCreateOrEditSuccess();
        }

    }, [addOrEditThresholdIsSuccess, addOrEditThresholdIsError, addOrEditThresholdIsFetching]);

    /**
     * Handles the success event when creating or editing a threshold.
     * If `onCreateSuccess` is provided and the create modal is open, it calls `onCreateSuccess`.
     * Closes the modal afterwards.
     */
    const handleCreateOrEditSuccess = () => {
        onCreateOrEditSuccess();
        handleCloseModal();
    }

    /**
     * Create or edit the threshold when the bodyToPost is set
     */
    useEffect(() => {
        if (bodyToPost) {
            refetchAddOrEditThreshold();
        }
    }, [bodyToPost])

    const handleOkModal = (data) => {
        const itemId = isEditModalOpen ? selectedItem.thresholdId : selectedItem.id;
        const bodyToPost = {
            "ID": itemId,
            "Label": data.label,
            "Type": data.type,
            "Open Threshold": data.openThreshold,
            "Close Threshold": data.closeThreshold ?? "",
            "Urgency": data.urgency,
            "CityService": selectedItem.cityService ?? selectedItem.class + "_" + itemId,
            "Event Description": data.eventDescription,
            "Is Default": data.isDefault ?? false,
            "Incident Service": data.incidentService ?? "",
            "Category": data.incidentCategory ?? "",
            "tenant": selectedItem.tenant,
            "Old Label": selectedItem.oldLabel ?? "",
            "Old Type": selectedItem.oldType ?? "",
            "Old Open Threshold": selectedItem.oldOpenThreshold ?? "",
        }
        setBodyToPost(bodyToPost);
    }

    const handleCloseModal = () => {
        if (isCreateModalOpen) {
            setCreateStep(1);
            setSelectedTenant(null);
            setBodyToPost(null);
            onCreateModalClose();
        } else if (isEditModalOpen) {
            setBodyToPost(null);
            onEditModalClose();
        } else {
            onViewModalClose();
        }

        removeAddOrEditThreshold();
        removeSubTenants();
        removeModalConfigs();
    }

    const customNavigationFooter = (form) => (
        <CustomNavigationFooter
            form={form}
            handleCloseModal={handleCloseModal}
            nextButtonDisable={selectedItem == null}
            onNextClick={() => {
                setCreateStep(2);
                refetchModalConfigs();
            }}
        />
    );

    const customActionFooter = (form) => (
        <CustomActionFooter
            form={form}
            handleCloseModal={onCreateModalClose}
            onBackClick={() => {
                setCreateStep(1);
            }}
            hideBackButton={isViewModalOpen || isEditModalOpen}
            onOkModal={(values) => {
                handleOkModal(values)
            }}
        />
    )

    const thresholdsCreateModal = useThresholdsCreateModal({
        setSelectedItem,
        subTenants: [tenant[0], ...(subTenants || [])],
        selectedTenant,
        setSelectedTenant,
        isToCleanUp: !isCreateModalOpen,
    })

    const thresholdsItemModal = useThresholdsItemModal({
        disableFields: isViewModalOpen,
        selectedItem,
        modalConfigs,
        selectedTenant,
        isCreateModalOpen,
        isEditModalOpen,
        itemName: isCreateModalOpen ? selectedItem?.name : selectedItem?.itemName,
    })

    const activeThresholdsModal = () => {
        if (isCreateModalOpen) {
            return createStep === 1
                ? thresholdsCreateModal
                : thresholdsItemModal
        } else {
            return thresholdsItemModal;
        }
    }

    const activeThresholdsFooter = () => {
        if (isCreateModalOpen) {
            return createStep === 1
                ? customNavigationFooter
                : customActionFooter
        } else {
            return customActionFooter;
        }
    }

    useEffect(() => {
        if (isCreateModalOpen) {
            refetchSubTenants();
        } else if (isEditModalOpen) {
            refetchModalConfigs();
        }
    }, [isCreateModalOpen, isEditModalOpen])

    return (
        <EditPopupConfigurations
            openModal={isCreateModalOpen || isEditModalOpen || isViewModalOpen}
            isToEdit={isEditModalOpen || isViewModalOpen}
            modalTitle={isViewModalOpen && t("Details")}
            closeModal={handleCloseModal}
            onOkModal={(data) => handleOkModal(data)}
            selectedRecord={selectedRecord ?
                serializeRecordForThresholdsModal(selectedRecord, { subTenants, modalConfigs }, activeThresholdsModal())
                : null}
            customWidth={1000}
            customFooter={activeThresholdsFooter()}
        />
    )
}

export default ThresholdsModal;